/**
 * FeatureAnnounce — the header "NEW" chip + speech bubble for feature updates.
 *
 * Renders a small brand "NEW" chip in the top bar when the signed-in user has any
 * undismissed announcement (audience-matched). A speech bubble points at the chip
 * with the announcement text; × (or "Got it") dismisses it for that user forever
 * (localStorage). One announcement shown at a time; dismissing reveals the next.
 *
 * This is the STANDARD format for every feature update — add entries to
 * `announcements.ts`, the chrome here doesn't change.
 */

import { useEffect, useRef, useState } from 'react';
import { useProfile } from '../profile/ProfileProvider';
import { ANNOUNCEMENTS } from './announcements';
import { X } from 'lucide-react';
import { Icon } from '../design/Icon';

const dismissKey = (id: string) => `daaily.announce.${id}`;

function loadDismissed(): Set<string> {
  const s = new Set<string>();
  try {
    for (const a of ANNOUNCEMENTS) {
      if (localStorage.getItem(dismissKey(a.id)) === '1') s.add(a.id);
    }
  } catch {
    /* ignore */
  }
  return s;
}

export function FeatureAnnounce() {
  const { state } = useProfile();
  const [dismissed, setDismissed] = useState<Set<string>>(loadDismissed);
  const [open, setOpen] = useState(true); // auto-open so it's noticed
  const ref = useRef<HTMLDivElement>(null);

  // Close the bubble (without dismissing) on an outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  if (state.status !== 'ready') return null;

  const active = ANNOUNCEMENTS.filter(
    (a) => a.audience(state.user) && !dismissed.has(a.id),
  );
  if (active.length === 0) return null;
  const current = active[0]!;

  const dismiss = (id: string) => {
    try {
      localStorage.setItem(dismissKey(id), '1');
    } catch {
      /* ignore */
    }
    setDismissed((prev) => new Set(prev).add(id));
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center rounded-full bg-brand-subtle text-brand text-2xs font-semibold px-2 py-0.5 hover:opacity-90"
        title="What’s new"
      >
        NEW
      </button>

      {open && (
        // Phones: full width under the top bar; anchored to the chip, the 320px
        // bubble hung off the left edge of the screen.
        <div className="absolute right-0 top-full mt-2 z-40 w-80 max-md:fixed max-md:inset-x-4 max-md:top-[calc(var(--app-header-h)+0.5rem)] max-md:mt-0 max-md:w-auto">
          {/* arrow pointing up to the chip */}
          <div className="absolute -top-1.5 right-3 h-3 w-3 rotate-45 bg-surface border-l border-t border-line max-md:hidden" />
          <div className="relative rounded-lg border border-line bg-surface text-fg shadow-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm font-semibold text-fg">{current.title}</div>
              <button
                type="button"
                onClick={() => dismiss(current.id)}
                aria-label="Dismiss"
                className="shrink-0 -mt-1 -mr-1 inline-flex h-6 w-6 items-center justify-center rounded-md text-fg-faint hover:text-fg hover:bg-surface-2"
              >
                <Icon as={X} />
              </button>
            </div>
            <p className="mt-1.5 text-sm text-fg-muted">{current.body}</p>
            <div className="mt-3 flex items-center justify-between">
              {active.length > 1 && (
                <span className="text-xs text-fg-faint">{active.length - 1} more</span>
              )}
              <button
                type="button"
                onClick={() => dismiss(current.id)}
                className="ml-auto btn-primary !py-1.5 !px-3 text-xs"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
